import { useAppDispatch, useAppSelector, useTranslate, useWidgetParam } from '@src/core/hooks';
import { changeLocation } from '@src/core/store';
import { FormInputInputType } from '@src/core/buyerApiClient';
import {
  currentOrderSelector,
  orderRequiredFormInputsSelector,
  sendOrderRequiredFormInput,
  SENDING,
  COMPLETED,
  BRAZIL_PHONE_DDI,
} from '@src/features/order';
import React, { useEffect, useReducer } from 'react';
import FilledButton from '../reusable/button/FilledButton';
import { device } from '@src/common/breakpoints';
import { BodyLarge, BodyLargeMedium, BodySmallMedium, Heading4, Heading6 } from '@src/fonts/typography/Typography';
import styled from 'styled-components';
import { BuyerCollectInfoFormInputs } from './BuyerCollectInfoFormInputs';
import { LayoutColors } from '@src/core/theme';
import { setStepCheckout } from '@src/features/checkout';
import { useWidgetId } from '@src/core/IdProvider';
import { track } from '@src/services/analytics';

export const BuyerInfoCollect = ({ proceedToNextStage }: BuyerInfoCollectProps) => {
  const orderRequiredFormInputs = useAppSelector(orderRequiredFormInputsSelector)!;
  const currentOrder = useAppSelector(currentOrderSelector);
  const dispatch = useAppDispatch();
  const checkoutPage = useWidgetParam('landing-page');
  const redirect = () => dispatch(changeLocation(checkoutPage || ''));
  const { i18nElement } = useTranslate();
  const id = useWidgetId();

  React.useEffect(() => {
    dispatch(setStepCheckout({ id, value: 'personal' }));
  }, [id, dispatch]);

  const [inputsDataToSend, handleInputDataToSend] = useReducer(
    (state: InputsReducerState, payload: FinalFormValuesToSend) => {
      if (
        payload.inputType === FormInputInputType.Phone &&
        !payload.value.includes(BRAZIL_PHONE_DDI) &&
        payload.value
      ) {
        payload.value = `${BRAZIL_PHONE_DDI}${payload.value}`;
      }

      return { ...state, [payload.hash]: payload };
    },
    {},
  );

  const finalInputs = Object.values(inputsDataToSend).filter((input) => input.value !== '');
  const isSending = orderRequiredFormInputs.state === SENDING;
  const isActionButtonDisabled = finalInputs.length !== orderRequiredFormInputs.qtd || isSending;

  const handleSubmit = () => {
    dispatch(sendOrderRequiredFormInput(finalInputs, currentOrder!));
  };

  useEffect(() => {
    if (orderRequiredFormInputs.state === COMPLETED) {
      proceedToNextStage();
    }
  }, [orderRequiredFormInputs.state]);

  useEffect(() => {
    track('checkoutBuyerInfoCollectAccess');
  }, []);

  return (
    <BuyerCollectInfoContainer>
      <HeaderTitle>{i18nElement('checkout_buyerInfoCollectHeaderTitle')}</HeaderTitle>
      <ContainerSubtitleText>{i18nElement('checkout_buyerInfoCollectSubtitle')}</ContainerSubtitleText>
      {orderRequiredFormInputs.inputs.map((formInputs, index) => (
        <BuyerCollectInfoFormInputs
          index={index}
          handleInputDataToSend={handleInputDataToSend}
          formInputs={formInputs}
          key={index}
        />
      ))}
      <ContainerActions>
        <FilledButton disabled={isActionButtonDisabled} bgColor={'#2D996F'} loading={isSending} onClick={handleSubmit}>
          {i18nElement('checkout_buyerInfoCollectProceed')}
        </FilledButton>
        <StepBackButton onClick={() => redirect()}>{i18nElement('checkout_buyerInfoCollectStepback')}</StepBackButton>
      </ContainerActions>
    </BuyerCollectInfoContainer>
  );
};

interface BuyerInfoCollectProps {
  proceedToNextStage: () => void;
}

interface InputsReducerState {
  [key: string]: FinalFormValuesToSend;
}

export interface FinalFormValuesToSend {
  hash: string;
  value: string;
  inputType: string;
}

const BuyerCollectInfoContainer = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 16px;
  display: flex;
  flex-flow: column;
  margin: 0 auto;
  background-color: ${LayoutColors.whiteDark};
  border-radius: 8px;
  gap: 16px;

  ${device.smallTablet} {
    padding: 24px;
    gap: 32px;
  }
`;

const HeaderTitle = styled.div`
  ${Heading6};
  color: ${LayoutColors.blackDark};

  ${device.smallTablet} {
    ${Heading4};
    color: ${LayoutColors.blackDark};
  }
`;

export const ContainerSubtitleText = styled.div`
  ${BodySmallMedium};
  color: ${LayoutColors.blackMain};

  ${device.smallTablet} {
    ${BodyLarge}
    color: ${LayoutColors.blackMain};
  }
`;

const ContainerActions = styled.div`
  display: flex;
  flex-flow: column;
  gap: 4px;
  margin-top: 24px;
`;

const StepBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: transparent;
  ${BodyLargeMedium};
  font-weight: 700;
  padding-top: 24px;
  border-style: none;
`;
